<template>
  <div class="archives-viewer container">
    <div class="row justify-content-center mt-4">
      <main class="col-md-8 col-sm-12 mt-2 pb-8">
        <!-- RESULTS TITLE -->
        <div class="product__header col-12 pr-0 mb-4">
          <div class="row px-0">
            <h1 class="col-12 display-3">
              {{ archivesCount }} {{ $t('knowledge.archives.label') }}
            </h1>
            <h3 class="col-12 text-muted">
              {{
                $t('knowledge.archives.knowledge-archives', {
                  knowledge: focusKnowledgeLabel,
                })
              }}
            </h3>
          </div>
        </div>

        <div class="position-relative">
          <TranslationDropdown
            class="dropdown-position"
            v-if="focusKnowledgeIsMultilinguale"
            :set-languages="focusKnowledgeLanguages"
            :editable="false"
          />
          <ArchivesViewerListSection
            v-loading="archivesLoading"
            :results="processedArchives"
            :counts="processArchivesCounts"
            :tabs="tabsConfig"
            @focus-archive="focusArchive"
            @bulk-delete="bulkDelete"
            @change-page="changePage"
            @launch-search="launchSearch"
          />
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ArchivesViewerListSection from './ArchivesViewerListSection';
import TranslationDropdown from '@/components/TranslationDropdownWrapper';

export default {
  name: 'archives-viewer',
  components: {
    ArchivesViewerListSection,
    TranslationDropdown,
  },
  data() {
    return {
      archivesLoading: false,
      loadingMessage: null,
      pages: 0,
      pageSize: 20,
      page: 1,
      archivesCount: 0,
      articles: {
        results: [],
        total: 0,
        pages: 0,
        pageSize: 20,
        page: 1,
        search: '',
      },
      diagnostics: {
        results: [],
        total: 0,
        pages: 0,
        pageSize: 20,
        page: 1,
        search: '',
      },
      cases: {
        results: [],
        total: 0,
        pages: 0,
        pageSize: 20,
        page: 1,
        search: '',
      },
    };
  },
  computed: {
    processedArchives() {
      return {
        cases: this.processArchive(this.cases, 'case'),
        articles: this.processArchive(this.articles, 'content'),
        diagnostics: this.processArchive(this.diagnostics, 'content'),
      };
    },
    processArchivesCounts() {
      return {
        cases: this.cases.total,
        articles: this.articles.total,
        diagnostics: this.diagnostics.total,
      };
    },
    tabsConfig() {
      return {
        cases: !this.isSplitKnowledgeViewer ? true : undefined,
        articles: true,
        diagnostics: true,
      };
    },
    entities() {
      return Object.keys(this.tabsConfig);
    },
    ...mapGetters('knowledgeModule', [
      'focusKnowledgeLabel',
      'focusKnowledgeValue',
      'focusKnowledgeDefaultLanguage',
      'focusKnowledgeLanguages',
      'editingLanguage',
      'focusKnowledgeIsMultilinguale',
    ]),
    ...mapGetters(['isSplitKnowledgeViewer']),
  },
  async created() {
    this.$emit('change-route-type', 'Archive');
    this.fetchArchives();
  },
  mounted() {
    if (this.$route.query.page) {
      this.page = parseInt(this.$route.query.page, 10);
    }
  },
  methods: {
    launchSearch({ search, entityType }) {
      this[entityType].search = search;
      this.loadEntityArchives(1, entityType, search);
    },
    async loadEntityArchives(page, entityType, search = '') {
      this.archivesLoading = true;

      const entityResults = await this.$services.archives.getArchives(
        this.focusKnowledgeValue,
        [entityType],
        page,
        this[entityType].pageSize,
        search,
        this.editingLanguage,
      );

      this[entityType] = Object.assign(
        {},
        this[entityType],
        entityResults[this.resolveEntityTypeGqlKey(entityType)],
      );

      this.archivesLoading = false;
    },
    async fetchArchives() {
      this.archivesLoading = true;
      const archivesResults = await this.$services.archives.getArchives(
        this.focusKnowledgeValue,
        this.entities,
        this.page,
        this.pageSize,
        this.search,
        this.editingLanguage,
      );

      const { archivedArticles, archivedDiagnostics, archivedCases } =
        archivesResults;

      this.articles = Object.assign({}, this.articles, archivedArticles);
      this.diagnostics = Object.assign(
        {},
        this.diagnostics,
        archivedDiagnostics,
      );
      this.cases = Object.assign({}, this.cases, archivedCases);

      this.archivesCount = Object.keys(this.processArchivesCounts).reduce(
        (acc, val) => {
          acc += this.processArchivesCounts[val];
          return acc;
        },
        0,
      );

      this.archivesLoading = false;
    },

    triggerBulkLoadingMessage(msg) {
      this.loadingMessage = this.$message({
        duration: 0,
        dangerouslyUseHTMLString: true,
        iconClass: 'display: none',
        showClose: true,
        message: `<i class="fas fa-spinner fa-spin" style="margin-right: 1em;"></i> ${msg}`,
      });
    },
    changePage({ page, entity }) {
      this.$router.push({
        query: Object.assign({}, this.$route.query, {
          page,
          entity,
          search: this[entity].search,
        }),
      });
    },
    focusArchive({ entity, entityType }) {
      const { archiveId, deletedCaseFrom } = entity;

      this.$router.push({
        path: this.resolveRouterForEntityFocus(
          archiveId,
          deletedCaseFrom,
          entityType,
        ),
      });
    },

    async bulkDelete({ entityIds, entityType }) {
      this.triggerBulkLoadingMessage(
        this.$t('knowledge.archives.modal.actions.delete-loading'),
      );

      let archives = [];

      try {
        archives = this.getArchiveIdsFromEntityIds(
          entityIds,
          this[entityType],
          entityType === 'cases' ? 'case' : 'content',
        );
      } catch (err) {
        return this.loadingMessage.close();
      }

      if (!archives || !archives.length) {
        return this.loadingMessage.close();
      }

      const archiveIds = archives.map((a) => a.id);

      if (entityType === 'cases') {
        await this.bulkDeleteCaseArchive(archiveIds);
      } else {
        await this.bulkDeleteContentArchive(archiveIds);
      }

      await this.fetchArchives();

      return this.loadingMessage.close();
    },

    // UTILS
    processArchive(archiveFilter, key) {
      const results = archiveFilter.results.length
        ? archiveFilter.results.map((a) => ({
            ...a[key],
            isFromCase: a.isFromCase,
            deletedCaseFromLabel: a.deletedCaseArchiveFrom
              ? a.deletedCaseArchiveFrom.case.label
              : null,
            isSelectable: !a.isFromCase,
            labelClass: a.isFromCase ? 'content-list-item-from-case' : '',
            deletedCaseFrom: a.deletedCaseArchiveFrom,
            archiveId: a.id,
          }))
        : [];
      return {
        ...archiveFilter,
        results,
      };
    },
    /**
     * This method aims to retrieve an archive from its entityId. An entity can be either
     * an article, a diagnostic or a case. Hence the entityList is list of archives for the given entity,
     * and the entityKey where we can find the entity infos within the archive.
     */
    getArchiveIdsFromEntityIds(ids, entityList, entityKey) {
      return ids
        .map((id) => entityList.results.find((a) => a[entityKey].id === id))
        .filter((el) => !!el);
    },
    resolveRouterForEntityFocus(archiveId, deletedCaseFrom, type) {
      let route = 'archive';
      let query = '';

      if (deletedCaseFrom) {
        const { id: mainArchiveId } = deletedCaseFrom;
        route = `${route}/case/${mainArchiveId}`;
        if (type !== 'cases') {
          route = `${route}/content/${archiveId}`;
        }
        query = `?target=${archiveId}`;
      } else {
        route =
          type === 'cases'
            ? `archive/case/${archiveId}`
            : `archive/${archiveId}`;
      }
      return `${route}${query}`;
    },
    resolveEntityTypeGqlKey(entityType) {
      return `archived${entityType[0].toUpperCase()}${entityType.slice(1)}`;
    },
    ...mapActions('knowledgeModule', [
      'bulkDeleteContentArchive',
      'bulkDeleteCaseArchive',
    ]),
  },
  watch: {
    $route() {
      const { page, entity, search } = this.$route.query;

      this.loadEntityArchives(page, entity, search);
    },
    editingLanguage() {
      this.fetchArchives();
    },
  },
};
</script>

<style lang="scss" scoped>
.search-result-icon {
  margin-right: 8px;
  max-width: 16px;
  max-height: 16px;
  cursor: pointer;
}

.section-title {
  color: $grey-5-mayday;
  cursor: pointer;
  margin-bottom: 0px;
  margin-top: 8px;
  margin-bottom: 8px;
}

.description-item {
  font-size: 80%;
  list-style-type: circle;
}

.trigger-alert-modal {
  color: $grey-5-mayday;
  cursor: pointer;
  &:hover {
    color: $blue-mayday;
  }
}

.carousel-main {
  max-height: 60vh;
  overflow: auto;
}

.trigger-slideshow {
  cursor: pointer;
  color: $grey-5-mayday;
  &:hover {
    color: $blue-mayday;
  }
}

.description-list {
  padding-inline-start: 16px;
}

.product__list {
  border-left: 3px solid $grey-4-mayday;
  padding-left: 10px !important;
  list-style-type: none;
}

.product__header {
  border-left: 3px solid $grey-4-mayday;
}
.product__button {
  background-color: transparent;
  border: none;
  color: $blue-mayday;
}

.product__button--collapsed {
  color: $grey-5-mayday;
}

.product__manual__collapsable,
.product__manual__item-row {
  width: 100%;

  a .product__manual__link {
    margin-top: 2px;
    margin-bottom: 2px;
    width: 100%;
    color: $grey-9-mayday;

    &:hover {
      background-color: $grey-1-mayday;
      border-radius: 2px;
    }
  }
}

.dropdown-position {
  position: absolute;
  right: 0px;
  top: 8px;
  z-index: 30;
}
</style>
